import type { PlanningSupplier, PriceBreakOption } from './types';

export interface LotSize {
  /** Stock units the order adds. */
  quantity: number;
  /** Purchase units on the PO (rolls); equals `quantity` when bought in the stock unit. */
  purchaseQuantity: number;
  moqApplied: boolean;
  multipleApplied: boolean;
}

/** Stock units in one of the supplier's purchase units. */
export function purchaseConversion(supplier: PlanningSupplier | null): number {
  const conv = supplier?.unitsPerPurchaseUom ?? 1;
  return Number.isFinite(conv) && conv > 0 ? conv : 1;
}

/** Stock units for a purchase-unit quantity (4 rolls → 7,200 in). */
export function stockUnitsFor(purchaseQuantity: number, supplier: PlanningSupplier | null): number {
  return Math.round(purchaseQuantity * purchaseConversion(supplier));
}

/**
 * Round a net requirement (stock units) up to what the vendor will
 * actually sell: whole purchase units, at least the MOQ, in multiples of
 * the pack size. 2,000 in of webbing sold in 1,800 in rolls is 2 rolls.
 */
export function applyLotRules(netRequirement: number, supplier: PlanningSupplier | null): LotSize {
  const conv = purchaseConversion(supplier);
  let purchaseQuantity = Math.max(0, Math.ceil(netRequirement / conv - 1e-9));
  let moqApplied = false;
  let multipleApplied = false;

  const moq = supplier?.minOrderQty ?? null;
  if (moq != null && moq > 0 && purchaseQuantity < moq) {
    purchaseQuantity = moq;
    moqApplied = true;
  }

  const multiple = supplier?.orderMultiple ?? null;
  if (multiple != null && multiple > 1 && purchaseQuantity % multiple !== 0) {
    purchaseQuantity = Math.ceil(purchaseQuantity / multiple) * multiple;
    multipleApplied = true;
  }

  return {
    quantity: stockUnitsFor(purchaseQuantity, supplier),
    purchaseQuantity,
    moqApplied,
    multipleApplied,
  };
}

/**
 * Cost per stock unit when ordering `quantity` stock units: the highest
 * price break reached, else the supplier's base price, else the catalog
 * standard cost (already per stock unit). Supplier prices are per
 * purchase unit, so a $45 roll of 1,800 in is 2.5¢ per inch.
 */
export function unitCostAt(
  quantity: number,
  supplier: PlanningSupplier | null,
  fallbackCents: number | null,
): number | null {
  if (!supplier) return fallbackCents;
  const conv = purchaseConversion(supplier);
  const purchaseQty = quantity / conv;
  let cost = supplier.unitCostCents;
  for (const brk of [...supplier.priceBreaks].sort((a, b) => a.minQty - b.minQty)) {
    if (purchaseQty + 1e-9 >= brk.minQty) cost = brk.unitCostCents;
  }
  return cost != null ? cost / conv : fallbackCents;
}

/**
 * Is there a price break above the suggested quantity worth showing?
 *
 * Picks the cheapest-per-unit break above `quantity` and reports what it
 * does to the total: `recommended` when the bigger order costs *less* in
 * total (the extra units are free, so there is no cash argument against
 * it), otherwise the operator weighs the unit saving against the extra
 * cash and shelf time. Quantities are stock units; break thresholds are
 * purchase units.
 */
export function evaluatePriceBreak(
  quantity: number,
  supplier: PlanningSupplier | null,
  fallbackCents: number | null,
  dailyUsage: number,
): PriceBreakOption | null {
  if (!supplier || supplier.priceBreaks.length === 0) return null;
  const conv = purchaseConversion(supplier);
  const purchaseQty = quantity / conv;
  const baseUnit = unitCostAt(quantity, supplier, fallbackCents);
  if (baseUnit == null || baseUnit <= 0) return null;

  const candidates = supplier.priceBreaks
    .filter((b) => b.minQty > purchaseQty + 1e-9 && b.unitCostCents / conv < baseUnit)
    .sort((a, b) => a.minQty - b.minQty);
  if (candidates.length === 0) return null;

  const multiple = supplier.orderMultiple ?? 1;
  const baseTotal = Math.round(quantity * baseUnit);
  let best: PriceBreakOption | null = null;
  for (const brk of candidates) {
    const purchaseQuantity = multiple > 1 ? Math.ceil(brk.minQty / multiple) * multiple : brk.minQty;
    const qty = stockUnitsFor(purchaseQuantity, supplier);
    const unit = unitCostAt(qty, supplier, fallbackCents) ?? brk.unitCostCents / conv;
    const total = Math.round(qty * unit);
    const option: PriceBreakOption = {
      quantity: qty,
      purchaseQuantity,
      unitCostCents: unit,
      totalCents: total,
      baseTotalCents: baseTotal,
      extraCashCents: total - baseTotal,
      unitSavingsPct: (baseUnit - unit) / baseUnit,
      extraCoverageDays: dailyUsage > 0 ? Math.round((qty - quantity) / dailyUsage) : 0,
      recommended: total <= baseTotal,
    };
    // Prefer a break that is a free win; otherwise the nearest one, which
    // ties up the least extra cash.
    if (!best || (option.recommended && !best.recommended)) best = option;
  }
  return best;
}
